import { supabase } from '../lib/supabase';
import type {
  Publisher,
  PublisherInsert,
  PublisherUpdate,
  Work,
  WorkInsert,
  WorkUpdate,
  CrowdfundingCampaign,
  CrowdfundingCampaignInsert,
  CrowdfundingCampaignUpdate,
} from '../types/slicetv';

// Base API utilities
export class APIError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public code?: string
  ) {
    super(message);
    this.name = 'APIError';
  }
}

/** Donor debits are stored as negative amounts; publisher revenue is the absolute value. */
function donationSliceCredit(amount: number | null | undefined): number {
  return Math.abs(amount ?? 0);
}

// Publisher API functions
export const publisherAPI = {
  // Get current publisher profile (founder or team membership)
  async getProfile(): Promise<Publisher> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new APIError('User not authenticated');

    const { data: owned, error: ownedError } = await supabase
      .from('publishers')
      .select('*')
      .eq('auth_user_id', user.id)
      .maybeSingle();

    if (ownedError) throw new APIError(ownedError.message);
    if (owned) return owned;

    const { data: memberRow, error: memberError } = await supabase
      .from('publisher_users')
      .select('publisher_id')
      .eq('auth_user_id', user.id)
      .eq('is_active', true)
      .order('created_at')
      .limit(1)
      .maybeSingle();

    if (memberError) throw new APIError(memberError.message);
    if (!memberRow) throw new APIError('Publisher not found');

    const { data: studio, error: studioError } = await supabase
      .from('publishers')
      .select('*')
      .eq('id', memberRow.publisher_id)
      .single();

    if (studioError) throw new APIError(studioError.message);
    return studio;
  },

  // Create publisher profile during onboarding
  async create(publisherData: PublisherInsert): Promise<Publisher> {
    const { data, error } = await supabase
      .from('publishers')
      .insert(publisherData)
      .select()
      .single();

    if (error) throw new APIError(error.message);
    return data;
  },

  // Update publisher profile
  async update(id: string, updates: PublisherUpdate): Promise<Publisher> {
    const { data, error } = await supabase
      .from('publishers')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new APIError(error.message);
    return data;
  },

  // Get dashboard metrics
  async getDashboardMetrics(publisherId: string): Promise<{
    totalEarnings: number;
    slicesReceived: number;
    monthlySlices: number;
    activeWorks: number;
    totalViews: number;
    activeCampaigns: number;
    completedCampaigns: number;
  }> {
    const { data: works, error: worksError } = await supabase
      .from('works')
      .select('id, view_count, status')
      .eq('publisher_id', publisherId);

    if (worksError) throw new APIError(worksError.message);

    const workIds = (works || []).map((w: any) => w.id);

    let totalSlices = 0;
    let monthlySlices = 0;
    let activeCampaignsCount = 0;
    let completedCampaignsCount = 0;

    if (workIds.length > 0) {
      const [slicesRes, campaignsRes] = await Promise.all([
        supabase
          .from('slice_transactions')
          .select('amount, created_at')
          .in('work_id', workIds),
        supabase
          .from('crowdfunding_campaigns')
          .select('status')
          .in('work_id', workIds),
      ]);

      if (slicesRes.error) throw new APIError(slicesRes.error.message);
      if (campaignsRes.error) throw new APIError(campaignsRes.error.message);

      const now = new Date();
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

      (slicesRes.data || []).forEach((tx: any) => {
        const credit = donationSliceCredit(tx.amount);
        totalSlices += credit;
        if (new Date(tx.created_at) >= monthStart) {
          monthlySlices += credit;
        }
      });

      (campaignsRes.data || []).forEach((c: any) => {
        if (c.status === 'active') activeCampaignsCount++;
        if (c.status === 'completed') completedCampaignsCount++;
      });
    }

    return {
      totalEarnings: totalSlices, // Each slice = $1 USD
      slicesReceived: totalSlices,
      monthlySlices,
      activeWorks: (works || []).filter((w: any) => w.status === 'published').length,
      totalViews: (works || []).reduce((sum: number, w: any) => sum + (w.view_count || 0), 0),
      activeCampaigns: activeCampaignsCount,
      completedCampaigns: completedCampaignsCount,
    };
  },

  // Get monthly earnings for chart (last 6 months)
  async getMonthlyEarnings(publisherId: string): Promise<{ date: string; slices: number }[]> {
    const { data: works } = await supabase
      .from('works')
      .select('id')
      .eq('publisher_id', publisherId);

    const workIds = (works || []).map((w: any) => w.id);

    const buckets: { date: string; slices: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i, 1);
      buckets.push({
        date: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`,
        slices: 0,
      });
    }

    if (workIds.length > 0) {
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6, 1);

      const { data: transactions } = await supabase
        .from('slice_transactions')
        .select('amount, created_at')
        .in('work_id', workIds)
        .gte('created_at', sixMonthsAgo.toISOString());

      (transactions || []).forEach((tx: any) => {
        const d = new Date(tx.created_at);
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
        const bucket = buckets.find(b => b.date === key);
        if (bucket) bucket.slices += donationSliceCredit(tx.amount);
      });
    }

    return buckets;
  },

  // Get recent activity feed
  async getRecentActivity(publisherId: string): Promise<{
    type: string;
    title: string;
    description: string;
    timestamp: string;
  }[]> {
    const { data: works } = await supabase
      .from('works')
      .select('id, title')
      .eq('publisher_id', publisherId);

    const workIds = (works || []).map((w: any) => w.id);
    const workTitles = Object.fromEntries((works || []).map((w: any) => [w.id, w.title]));
    const activities: { type: string; title: string; description: string; timestamp: string }[] = [];

    if (workIds.length > 0) {
      const [recentSlices, recentCampaigns] = await Promise.all([
        supabase
          .from('slice_transactions')
          .select('amount, created_at, work_id')
          .in('work_id', workIds)
          .order('created_at', { ascending: false })
          .limit(5),
        supabase
          .from('crowdfunding_campaigns')
          .select('status, total_slices_raised, goal_slices, updated_at, work_id, works(title)')
          .in('work_id', workIds)
          .order('updated_at', { ascending: false })
          .limit(5),
      ]);

      (recentSlices.data || []).forEach((tx: any) => {
        const credit = donationSliceCredit(tx.amount);
        activities.push({
          type: 'slice_donation',
          title: `Received ${credit} slice${credit !== 1 ? 's' : ''}`,
          description: workTitles[tx.work_id] || 'Unknown work',
          timestamp: tx.created_at,
        });
      });

      (recentCampaigns.data || []).forEach((c: any) => {
        const pct = c.goal_slices > 0
          ? Math.round(((c.total_slices_raised || 0) / c.goal_slices) * 100)
          : 0;
        activities.push({
          type: 'campaign_update',
          title: `Campaign ${c.status === 'completed' ? 'completed' : `at ${pct}% funding`}`,
          description: c.works?.title || workTitles[c.work_id] || 'Unknown campaign',
          timestamp: c.updated_at,
        });
      });
    }

    activities.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return activities.slice(0, 10);
  },
};

// Works API functions
export const worksAPI = {
  // Get all works for publisher
  async getByPublisher(publisherId: string): Promise<Work[]> {
    const { data, error } = await supabase
      .from('works')
      .select('*')
      .eq('publisher_id', publisherId)
      .order('created_at', { ascending: false });

    if (error) throw new APIError(error.message);
    return data || [];
  },

  // Create new work
  async create(workData: WorkInsert): Promise<Work> {
    const { data, error } = await supabase
      .from('works')
      .insert(workData)
      .select()
      .single();

    if (error) throw new APIError(error.message);
    return data;
  },

  // Update work
  async update(id: string, updates: WorkUpdate): Promise<Work> {
    const { data, error } = await supabase
      .from('works')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new APIError(error.message);
    return data;
  },

  // Delete work
  async delete(id: string): Promise<void> {
    const { error } = await supabase.from('works').delete().eq('id', id);

    if (error) throw new APIError(error.message);
  },

  // Calculate upload fee
  async calculateUploadFee(durationMinutes: number): Promise<{
    feeAmount: number;
    ratePerMinute: number;
  }> {
    const ratePerMinute = 1.0; // $1 per minute
    return {
      feeAmount: durationMinutes * ratePerMinute,
      ratePerMinute,
    };
  },
};

// Campaigns API functions
export const campaignsAPI = {
  // Get campaigns for publisher
  async getByPublisher(publisherId: string): Promise<CrowdfundingCampaign[]> {
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .select(
        `
        *,
        works!inner(publisher_id, title, type)
      `
      )
      .eq('works.publisher_id', publisherId)
      .order('created_at', { ascending: false });

    if (error) throw new APIError(error.message);
    return data || [];
  },

  // Create campaign
  async create(
    campaignData: CrowdfundingCampaignInsert
  ): Promise<CrowdfundingCampaign> {
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .insert(campaignData)
      .select()
      .single();

    if (error) throw new APIError(error.message);
    return data;
  },

  // Update campaign
  async update(
    id: string,
    updates: CrowdfundingCampaignUpdate
  ): Promise<CrowdfundingCampaign> {
    const { data, error } = await supabase
      .from('crowdfunding_campaigns')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw new APIError(error.message);
    return data;
  },

  // Get campaign backers
  async getBackers(campaignId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('campaign_backers')
      .select(
        `
        *,
        profiles!inner(full_name, email)
      `
      )
      .eq('campaign_id', campaignId)
      .order('backed_at', { ascending: false });

    if (error) throw new APIError(error.message);
    return data || [];
  },
};

// Upload fees API
export const uploadFeesAPI = {
  // Calculate fee for content duration
  async calculateFee(
    durationMinutes: number,
    contentType: 'movie' | 'episode' | 'series'
  ): Promise<{
    feeAmount: number;
    durationMinutes: number;
    ratePerMinute: number;
    feeType: string;
  }> {
    const ratePerMinute = 1.0; // $1 per minute
    return {
      feeAmount: durationMinutes * ratePerMinute,
      durationMinutes,
      ratePerMinute,
      feeType: `${contentType}_upload`,
    };
  },

  // Create fee record
  async createFeeRecord(
    publisherId: string,
    workId: string,
    feeData: {
      amount_usd: number;
      duration_minutes: number;
      fee_type: string;
      description?: string;
    }
  ): Promise<any> {
    const { data, error } = await supabase
      .from('content_upload_fees')
      .insert({
        publisher_id: publisherId,
        work_id: workId,
        rate_per_minute: 1.0,
        ...feeData,
      })
      .select()
      .single();

    if (error) throw new APIError(error.message);
    return data;
  },
};

// Slice transactions API
export const slicesAPI = {
  // Get slice transactions for publisher
  async getTransactionsByPublisher(publisherId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('slice_transactions')
      .select(
        `
        *,
        campaign_backers!inner(
          campaign_id,
          crowdfunding_campaigns!inner(
            publisher_id,
            title
          )
        )
      `
      )
      .eq('campaign_backers.crowdfunding_campaigns.publisher_id', publisherId)
      .order('created_at', { ascending: false });

    if (error) throw new APIError(error.message);
    return data || [];
  },
};

// Export all APIs
export const api = {
  publisher: publisherAPI,
  works: worksAPI,
  campaigns: campaignsAPI,
  uploadFees: uploadFeesAPI,
  slices: slicesAPI,
};

export default api;
